import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articlesData } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articlesData.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articlesData.filter((a) => article.relatedArticles.includes(a.id));

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: '/category/dianshiju' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="pb-14 md:pb-20 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                <span className="text-foreground-400 text-xs">·</span>
                <span className="text-foreground-400 text-xs">阅读 {article.readTime}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>作者：{article.author}</span>
              </div>
            </header>

            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-10 bg-background-200">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((block, i) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={i} className="text-foreground-600 leading-relaxed mb-5 text-sm md:text-base">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14 pt-8 border-t border-background-200/70">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group p-4 rounded-xl border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 cursor-pointer transition-all duration-300"
                    >
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-400 line-clamp-2 mb-3">{related.summary}</p>
                      <div className="flex items-center gap-2 text-xs text-foreground-300">
                        <span>{related.category}</span>
                        <span>·</span>
                        <span>{related.readTime}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}